/**
 * UK tax year runs 6 April - 5 April. Used by the "Update my plan" rebaseline
 * flow (#1421) to decide whether to re-prompt for ISA allowance already used
 * this tax year — the engine otherwise assumes the full £20k is available in
 * Year 1 regardless of what month the plan was (re)started in.
 *
 * All arithmetic is done in UTC. Callers pass date-only ISO strings (e.g.
 * "2025-04-06"), which the Date constructor parses as UTC midnight — mixing
 * that with a locally-constructed Date would make the two silently disagree
 * by the local UTC offset (e.g. 6 April 01:00 BST vs UTC midnight).
 */

/** The most recent 6 April (UTC midnight) on or before the given date. */
export function currentUkTaxYearStart(date: Date): Date {
  const year = date.getUTCFullYear();
  const aprilSixThisCalendarYear = Date.UTC(year, 3, 6); // month index 3 = April
  return new Date(date.getTime() >= aprilSixThisCalendarYear ? aprilSixThisCalendarYear : Date.UTC(year - 1, 3, 6));
}

/**
 * True unless the given ISO date IS the UK tax year's first day (6 April) —
 * i.e. true for virtually every rebaseline, since a fresh 6 April start is
 * the only date where "allowance used this tax year" is definitionally 0.
 */
export function isMidTaxYear(isoDate: string): boolean {
  const date = new Date(isoDate);
  const start = currentUkTaxYearStart(date);
  return date.getTime() !== start.getTime();
}

/**
 * True when income/contributions "already received/made before the plan
 * start" fall in the same UK tax year the engine bands as Year 1.
 *
 * A plan starting 1 Jan-5 Apr means anything received before it sits in the
 * tax year that began the PREVIOUS April (currentUkTaxYearStart lands in the
 * previous calendar year), while engine year 1 is banded as the one
 * beginning the COMING April. The engine drops the seed in that case
 * (calculatePriorTaxYearIncome), so the wizard must not offer inputs that
 * would silently do nothing — callers should swap them for an explanation
 * instead. Shared by the desktop and mobile Step 2 wizard components so the
 * day-accurate 5/6 April boundary can't drift between them.
 */
export function priorIncomeAppliesToPlanYear(planAsOfDate: string | undefined): boolean {
  if (!planAsOfDate) return true;
  const asOf = new Date(planAsOfDate);
  return currentUkTaxYearStart(asOf).getUTCFullYear() === asOf.getUTCFullYear();
}
